import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import { useLoadable, ErrorBlock, EmptyBlock, Pill, RequestOutcomeBlock, type RequestOutcome } from '../components/ui'
import type { OwnerFolder, OwnersResponse } from '../types'

/* The full folder map, visible to every member of staff regardless of entitlement.
   Folder names are not secret from colleagues, and showing the whole map is what
   makes a request flow possible at all. It opens on "My area" (the caller's OpCo +
   department, plus the Company Hub and Group drives, worked out by the API from
   their groups); "Whole company" is one click away. A view filter, not access control. */

const REASON = 'Requested from Who owns what'

/* Owner Centre R1-1: propose who owns a folder. A named leader approves it on the
   Register page; until then the owner shown stays as it is. */
function ProposeOwner({ folder, onClose }: { folder: OwnerFolder; onClose: () => void }) {
  const [owner, setOwner] = useState('')
  const [deputy, setDeputy] = useState('')
  const [reason, setReason] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done' | { error: string }>('idle')
  const submit = async () => {
    setState('sending')
    try {
      await api.proposeOwner(folder.folderId, owner.trim(), deputy.trim(), reason.trim())
      setState('done')
    } catch (err) {
      setState({ error: err instanceof Error ? err.message : 'Could not send the proposal.' })
    }
  }
  if (state === 'done') {
    return (
      <>
        <Pill tone="neutral">Proposed, waiting for approval</Pill>
        <p style={{ marginTop: 'var(--s-sm)', marginBottom: 0 }}>
          A register approver will confirm it. The owner shown here changes once they approve.
        </p>
      </>
    )
  }
  const ready = /@dlight\.(com|co\.tz)$/i.test(owner.trim()) && reason.trim().length >= 10
  return (
    <div style={{ display: 'grid', gap: 'var(--s-sm)', maxWidth: 560 }}>
      <p style={{ margin: 0 }}><b>Propose an owner for {folder.name}</b> ({folder.driveName})</p>
      <label htmlFor={`po-${folder.folderId}`}>Owner's d.light email</label>
      <input className="field" id={`po-${folder.folderId}`} value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="name.surname@dlight.com" />
      <label htmlFor={`pd-${folder.folderId}`}>Deputy's email (optional)</label>
      <input className="field" id={`pd-${folder.folderId}`} value={deputy} onChange={(e) => setDeputy(e.target.value)} placeholder="Covers when the owner is away" />
      <label htmlFor={`pr-${folder.folderId}`}>Why this person (at least 10 characters)</label>
      <input className="field" id={`pr-${folder.folderId}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Heads recruitment in Kenya" />
      <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap' }}>
        <button className="btn btn--primary" disabled={!ready || state === 'sending'} onClick={submit}>
          {state === 'sending' ? 'Sending…' : 'Send for approval'}
        </button>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
      </div>
      {typeof state === 'object' && <p role="alert" style={{ margin: 0 }}><Pill tone="red">Not sent</Pill> {state.error}</p>}
    </div>
  )
}

function OwnerCell({ f }: { f: OwnerFolder }) {
  if (f.ownerStatus === 'missing') return <span className="note">Owner assignment required</span>
  return (
    <>
      {f.accountableOwner}
      {f.ownerStatus === 'provisional' && <><br /><Pill>Provisional</Pill></>}
      {f.deputy && <><br /><span className="note">Deputy: {f.deputy.name}</span></>}
    </>
  )
}

export default function OwnersRoute() {
  const [cat, retry] = useLoadable<OwnersResponse>(() => api.getOwners(), (d) => d.folders.length === 0, [])
  const [proposing, setProposing] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [wholeCompany, setWholeCompany] = useState(false)
  const hasArea = cat.status === 'ready' && cat.data.myArea === 'mine'
  const showArea = hasArea && !wholeCompany
  const [requested, setRequested] = useState<Record<string, RequestOutcome | 'sending'>>({})

  const onRequest = async (f: OwnerFolder) => {
    setRequested((r) => ({ ...r, [f.folderId]: 'sending' }))
    try {
      const res = await api.requestAccess(f.folderId, REASON)
      setRequested((r) => ({ ...r, [f.folderId]: res }))
    } catch (err) {
      setRequested((r) => ({
        ...r,
        [f.folderId]: { ok: false, delivered: [], failed: true, message: err instanceof Error ? err.message : 'Request failed.' },
      }))
    }
  }

  const [rows, wholeCompanyMatches] = useMemo(() => {
    if (cat.status !== 'ready') return [[], 0]
    const n = q.trim().toLowerCase()
    const all = [...cat.data.folders].sort((a, b) => a.driveOrder - b.driveOrder || a.name.localeCompare(b.name))
    const matching = n
      ? all.filter((f) => `${f.name} ${f.driveName} ${f.accountableOwner} ${f.whatBelongsHere}`.toLowerCase().includes(n))
      : all
    return [showArea ? matching.filter((f) => f.inMyArea) : matching, matching.length]
  }, [cat, q, showArea])

  if (cat.status === 'error') return <ErrorBlock message={cat.message} correlationId={cat.correlationId} onRetry={retry} />

  /* An empty catalogue is a setup problem, not "no folder matches your search"
     (rows.length === 0 below, which stays an EmptyBlock since that's normal).
     Without this, the page silently rendered nothing past the search box. */
  if (cat.status === 'empty') {
    return <ErrorBlock message="The folder catalogue is empty right now." onRetry={retry} />
  }

  return (
    <>
      <p className="eyebrow">Who owns what</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>Every folder has one owner</h1>
      <p className="note" style={{ marginBottom: 'var(--s-md)' }}>
        If you are unsure who to ask about a piece of content, start here. It opens on
        <b> My area</b>: your department in your country, plus the Company Hub and Group
        folders. <b>Whole company</b> shows every folder, including ones you cannot open. If you need one of them, use
        Request access: the portal records it and writes the email to the owner for you. Moving a whole folder into RMS?{' '}
        Open <Link to="/my-access">My access</Link>, select the destination folder and click Move a folder here.
      </p>
      <p className="note" style={{ marginBottom: 'var(--s-md)' }}>
        <b>Provisional</b> means the owner is taken from who manages the folder's access group and
        has not been confirmed yet. If it is wrong, or the folder says <b>Owner assignment required</b>,
        use Propose owner: a register approver confirms it.
      </p>

      {hasArea && (
        <div className="opts" role="group" aria-label="Which folders to show" style={{ marginBottom: 'var(--s-md)' }}>
          <button className="opt" aria-pressed={!wholeCompany} onClick={() => setWholeCompany(false)}>My area</button>
          <button className="opt" aria-pressed={wholeCompany} onClick={() => setWholeCompany(true)}>Whole company</button>
        </div>
      )}
      {cat.status === 'ready' && cat.data.myArea === 'unavailable' && (
        <p className="note" style={{ marginBottom: 'var(--s-md)' }}>
          Showing the whole company: your area could not be worked out just now.
        </p>
      )}

      <div className="search">
        <label className="sr-only" htmlFor="ownq">Filter folders</label>
        <input id="ownq" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="contracts, procurement, credit risk…" autoComplete="off" />
      </div>

      {cat.status === 'loading' && (
        <div className="tablewrap"><div style={{ padding: 'var(--s-md)' }} aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => <div className="skeleton" key={i} style={{ height: '1.1rem', marginBottom: 10 }} />)}
        </div></div>
      )}

      {cat.status === 'ready' && rows.length === 0 && showArea && wholeCompanyMatches > 0 && (
        <EmptyBlock title={`Nothing in your area matches "${q}"`}
          action={<button className="btn btn--ghost" onClick={() => setWholeCompany(true)}>Show the whole company →</button>}>
          {wholeCompanyMatches} {wholeCompanyMatches === 1 ? 'folder elsewhere matches' : 'folders elsewhere match'}.
        </EmptyBlock>
      )}

      {cat.status === 'ready' && rows.length === 0 && !(showArea && wholeCompanyMatches > 0) && (
        <EmptyBlock title={`No folder matches "${q}"`}>
          Try a department name, a document type, or the kind of thing you are looking
          for. The map covers every folder across all twelve drives.
        </EmptyBlock>
      )}

      {cat.status === 'ready' && rows.length > 0 && (
        <div className="tablewrap">
          <table>
            <thead>
              <tr><th>Folder</th><th>Drive</th><th>Owner</th><th>What belongs here</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((f) => {
                const outcome = requested[f.folderId]
                /* No Request access on a folder the caller can already open. */
                const canRequest = f.canOpen !== true && (!outcome || (outcome !== 'sending' && 'failed' in outcome))
                return (
                <Fragment key={f.folderId}>
                <tr>
                  <td style={{ color: 'var(--ink)', fontWeight: 500, whiteSpace: 'nowrap' }}>{f.name}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {f.driveName}<br /><Pill>{f.confidentiality}</Pill>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}><OwnerCell f={f} /></td>
                  <td style={{ minWidth: 260 }}>{f.whatBelongsHere}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <a className="btn btn--ghost" href={f.webViewLink} target="_blank" rel="noreferrer"
                       title="Opens in Drive. Folders you have not been granted will not open">
                      Open in Drive →
                    </a>
                    {canRequest && (
                      <>
                        <br />
                        <button className="btn btn--ghost" onClick={() => onRequest(f)}>
                          Request access →
                        </button>
                      </>
                    )}
                    {outcome === 'sending' && <><br /><span className="note">Recording…</span></>}
                    {f.ownerStatus !== 'approved' && proposing !== f.folderId && (
                      <>
                        <br />
                        <button className="btn btn--ghost" onClick={() => setProposing(f.folderId)}>Propose owner →</button>
                      </>
                    )}
                  </td>
                </tr>
                {proposing === f.folderId && (
                  <tr>
                    <td colSpan={5} style={{ whiteSpace: 'normal' }}>
                      <ProposeOwner folder={f} onClose={() => setProposing(null)} />
                    </td>
                  </tr>
                )}
                {outcome && outcome !== 'sending' && (
                  <tr>
                    <td colSpan={5} style={{ whiteSpace: 'normal' }}>
                      <RequestOutcomeBlock outcome={outcome} />
                    </td>
                  </tr>
                )}
                </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
